import React from 'react';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableHead from '@mui/material/TableHead';
import MuiTableRow from '@mui/material/TableRow';
import MuiTableContainer, {
  TableContainerProps as MuiTableContainerProps,
} from '@mui/material/TableContainer';
import { Validate } from '@dewise-devex/backstage-plugin-utilities-common-client';
import MuiTable, { TableProps as MuiTableProps } from '@mui/material/Table';
import { SvgIconTypeMap } from '@mui/material';
import { Typography } from '../typography';
import MuiTableCell from '@mui/material/TableCell';
import { OverridableComponent } from '@mui/material/OverridableComponent';

export interface TableProps extends MuiTableProps {
  /** An array of columns to display in the table, each with a title and corresponding accessor name. */
  columns: TableColumn[];
  /** An array of data to display in the table, each containing key-value pairs for the column accessors. */
  data: Record<string, any>[];
  /** Optional icon map to associate icons with specific column accessors, allowing visual representation. */
  iconMap?: Record<string, OverridableComponent<SvgIconTypeMap<any, 'svg'>>>;
  /** Optional component to render when the data array is empty. */
  EmptyComponent?: React.ReactElement;
  /** Optional props to pass to the MUI TableContainer component */
  containerProps?: MuiTableContainerProps;
}

/**
 * `TableColumn` defines the structure for each column in the table displayed within the `InformationCard`.
 * It includes the title of the column, the accessor name to retrieve data, and an optional icon accessor.
 */
export type TableColumn = {
  /** Title of the column, displayed as a header in the table. */
  title: string;
  /** Accessor name to retrieve the data for this column from the row object. */
  accessor: string;
  /** Optional accessor name to retrieve the icon from the icon map, if different from the column accessor. */
  iconAccessor?: string;
  /** Optional configuration for handling cell clicks */
  onCellClick?: (row: Record<string, any>) => void;
  /** Optional flag to indicate if the column is the primary column, used for styling or interaction purposes. */
  primary?: boolean;
};

export const Table = ({
  containerProps,
  ...props
}: TableProps): JSX.Element => {
  if (Validate.isEmptyArray(props.data)) {
    if (props.EmptyComponent) {
      return props.EmptyComponent;
    }
    return <Typography>No data to display</Typography>;
  }
  return (
    <MuiTableContainer component={'div'} {...containerProps}>
      <MuiTable>
        {/* Render table header with column titles */}
        <MuiTableHead>
          <MuiTableRow>
            {props.columns.map(column => (
              <MuiTableCell key={column.accessor}>
                <Typography fontSize={14} fontWeight="medium">
                  {column.title}
                </Typography>
              </MuiTableCell>
            ))}
          </MuiTableRow>
        </MuiTableHead>

        {/* Render table data with cell data and optional icons */}
        <MuiTableBody>
          {props.data.map((row, rowIndex) => (
            <MuiTableRow key={rowIndex}>
              {props.columns.map((column, cellIndex) => {
                const cellValue = row[column.accessor];
                const Icon =
                  column.iconAccessor && props.iconMap
                    ? props.iconMap[row[column.iconAccessor]]
                    : null;
                const onCellClick = column.onCellClick
                  ? () => column.onCellClick!(row)
                  : undefined;
                return (
                  <MuiTableCell
                    key={cellIndex}
                    onClick={onCellClick}
                    color={column.primary ? 'primary' : undefined}
                  >
                    {Icon ? (
                      <div>
                        <Icon sx={{ width: 18, height: 18 }} />
                        <span>{cellValue}</span>
                      </div>
                    ) : (
                      cellValue
                    )}
                  </MuiTableCell>
                );
              })}
            </MuiTableRow>
          ))}
        </MuiTableBody>
      </MuiTable>
    </MuiTableContainer>
  );
};
